<script lang="ts" setup>
import { computed, inject, ref, unref } from 'vue'
import type { ScrollChapter } from '../composables/useInfiniteChapter'
import type { ChapterConfig } from '@/composables/payment'
import { PaymentInlinePanel } from '@/composables/payment'
import { CHARGE_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'
import { brand } from '@/config/brands'

interface Props {
  chapter: ScrollChapter
  memberCoin: number
  contentId: string
  bookName?: string
  serialStatus?: number
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'unlock'): void
  (e: 'pay-success'): void
}>()

const config = inject<ChapterConfig>('chapterConfig')!
const theme = inject<any>('theme', { background: '#F6F7F9' })
const autoLock = ref(true)
const membershipOnly = ref(false)
const { t } = useI18n()
const themeBackground = computed(() => unref(theme)?.background?.toUpperCase?.() || '#F6F7F9')
const isDarkTheme = computed(() => {
  const bg = themeBackground.value
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(bg || '')
  if (!m)
    return false
  const r = Number.parseInt(m[1], 16)
  const g = Number.parseInt(m[2], 16)
  const b = Number.parseInt(m[3], 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) < 80
})
const balanceEnough = computed(() => props.memberCoin >= props.chapter.priceAmount)
const unlockDividerLockIcon = computed(() => `url(/images/${brand}/chapter/unlock-divider-lock.svg)`)

function handleUnlock() {
  emit('unlock')
}

function toggleAutoLock() {
  autoLock.value = !autoLock.value
}

function handlePaySuccess() {
  emit('pay-success')
}

defineExpose({
  autoLock,
})
</script>

<template>
  <div class="chapter-unlock relative pt-40px text-center" :class="{ 'rr-chapter-unlock': !isDarkTheme }">
    <div class="mask" />

    <div class="unlock-content">
      <!-- 装饰分割线 + 锁图标 -->
      <div class="rr-unlock-divider flex items-center justify-center mb-16px" aria-hidden="true">
        <span class="rr-unlock-divider__line" />
        <span class="rr-unlock-divider__icon" />
        <span class="rr-unlock-divider__line" />
      </div>

      <!-- 仅订阅(畅读会员)场景: 统一文案, 不展示金币解锁相关 -->
      <template v-if="membershipOnly">
        <h3 class="theme-h3 rr-unlock-title font-bold text-18px text-center">
          {{ config.texts.unlockTitle }}
        </h3>
        <p class="theme-desc mt-8px lh-24px text-14px text-center" style="white-space: pre-line;">
          {{ t('UnlockMembership') }}
        </p>
      </template>
      <template v-else>
        <h3 class="theme-h3 rr-unlock-title font-bold text-18px text-center">
          {{ config.texts.unlockTitle }}
        </h3>
        <p class="theme-desc rr-unlock-desc mt-8px lh-24px text-14px text-center" style="white-space: pre-line;">
          <template v-if="chapter.feeType === CHARGE_TYPE.CHAPTER">
            {{ t('CoinsToRead2', { 0: chapter.priceAmount }) }}
          </template>
          <template v-else>
            {{ t('CoinsToReadBook', { 0: chapter.priceAmount }) }}
          </template>
        </p>
      </template>

      <!-- 余额足够: Unlock 按钮 -->
      <template v-if="balanceEnough && !membershipOnly">
        <a
          class="theme-btn rr-unlock-btn inline-block mt-24px px-24px py-3 min-h-40px lh-1em text-15px text-white text-center btn-solid rounded-full w-300px"
          @click.stop="handleUnlock"
        >
          <span v-if="chapter.priceAmount !== undefined">
            <span v-if="chapter.feeType === CHARGE_TYPE.CHAPTER">{{ t('CoinsToRead2', { 0: chapter.priceAmount }) }}</span>
            <span v-else>{{ t('CoinsToReadBook', { 0: chapter.priceAmount }) }}</span>
          </span>
        </a>
      </template>

      <!-- 余额不足: 常驻支付版位 -->
      <template v-else>
        <div class="mt-24px" :class="{ 'pb-18px': membershipOnly }">
          <PaymentInlinePanel
            :content-id="contentId"
            :content-chapter-id="chapter.id"
            :book-name="bookName || ''"
            :chapter-name="chapter.name"
            :serial-status="serialStatus || 0"
            :dark="isDarkTheme"
            @success="handlePaySuccess"
            @update:membership-only="membershipOnly = $event"
          />
        </div>
      </template>

      <!-- Auto unlock 开关(仅订阅场景隐藏) -->
      <div v-if="!membershipOnly" class="theme-radio mt-24px flex justify-center items-center" @click.stop="toggleAutoLock">
        <div class="radio rr-unlock-radio flex justify-center items-center w-14px h-14px inline-block border b-rd-14px cursor" :class="{ selected: autoLock }">
          <span class="inline-block w-6px h-6px bg-n-primary b-rd-6px" />
        </div>
        <span class="ml-6px label rr-unlock-label text-14px">{{ config.texts.autoUnlock }}</span>
      </div>

      <!-- Coins Balance(仅订阅场景隐藏) -->
      <p v-if="!membershipOnly" class="theme-balance rr-unlock-balance mt-12px pb-18px lh-24px text-14px text-center">
        {{ t('NovelBalance', { 0: memberCoin }) }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.radio {
  span {
    display: none;
  }
  &.selected {
    border-color: var(--primary-color);
    span {
      display: inline-block
    }
  }
}

.chapter-unlock {
  position: relative;

  .mask {
    position: absolute;
    top: -126px;
    left: 0;
    width: 100%;
    height: 126px;
    background: linear-gradient(to bottom, transparent, var(--theme-background, #F6F7F9));
    pointer-events: none;
  }

  .unlock-content {
    position: relative;
    z-index: 1;
  }
}

.rr-chapter-unlock {
  .mask {
    background-image: var(--mask-gradient);
  }
}

.rr-unlock-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
}

.rr-unlock-divider__line {
  width: 80px;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--theme-balance-color, rgba(146, 150, 160, 0.4)), transparent);
  opacity: 0.5;
}

.rr-unlock-divider__line:first-child {
  background: linear-gradient(to left, transparent, var(--theme-balance-color, rgba(146, 150, 160, 0.4)), transparent);
  opacity: 0.5;
}

.rr-unlock-divider__icon {
  width: 16px;
  height: 18px;
  display: inline-block;
  flex-shrink: 0;
  background-color: var(--theme-balance-color, #9296A0);
  -webkit-mask-image: v-bind(unlockDividerLockIcon);
  mask-image: v-bind(unlockDividerLockIcon);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.rr-unlock-title {
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  color: var(--chapter-text-color, currentColor) !important;
  -webkit-text-fill-color: var(--chapter-text-color, currentColor);
}

.rr-unlock-btn {
  border: 0;
  font-size: 16px;
  font-weight: 700;

  &, * {
    color: #fff !important;
    -webkit-text-fill-color: #fff;
  }
}

.rr-unlock-radio {
  span {
    background: var(--theme-radio-bg, var(--primary-color)) !important;
  }
}

.rr-unlock-label {
  color: var(--chapter-text-color, currentColor) !important;
  -webkit-text-fill-color: var(--chapter-text-color, currentColor);
}
</style>
